import CallIcon from "@mui/icons-material/Call";
import CallEndIcon from "@mui/icons-material/CallEnd";
import { Typography, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

import { createLogger } from "@/logger";

const log = createLogger("call");

import React, { useEffect, useState } from "react";
import useSound from "use-sound";

import boopSfx from "@/audio/rings.mp3";
import SimpleModal from "@/components/SimpleModal";
import TimeBox from "@/components/TimeBox";
import { CallConfigurationState, useCall } from "@/contexts/CallContext.tsx";
import { AppContext } from "@/models/AppStateProvider";
import { TrackingContext } from "@/models/TrackingStateProvider.tsx";
import { getPaddedTime } from "@/utils";

const maxCallTime = 45;

export default function MakeCall({
  feedId,
  fromAccountId,
  toAccountId,
  disabled,
  name,
  handsFreeEnabled,
  offline,
}: {
  feedId: string;
  fromAccountId: string;
  toAccountId: string;
  disabled: boolean;
  name: string;
  handsFreeEnabled: boolean;
  offline: boolean;
}) {
  const { ampli } = React.useContext(TrackingContext);
  const [play, { stop }] = useSound(boopSfx, { volume: 0.5, loop: true });
  const { createCallAndListen, callTextStatus, disconnectCall } = useCall();
  const theme = useTheme();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [showRingingTimeBox, setShowRingingTimeBox] = useState<boolean>(false);
  const [ringingSeconds, setRingingSeconds] = useState<number>(0);
  const [showCallTimeBox, setShowCallTimeBox] = useState<boolean>(false);
  const [activeCallRecord, setActiveCallRecord] = useState<boolean>(null);
  const [callTimeSeconds, setCallTimeSeconds] = useState<number>(0);
  const [activeCall, setActiveCall] = useState<boolean>(false);
  const [disabledButton, setDisabledButton] = useState<boolean>(disabled);
  const disabledState = disabledButton || disabled || offline;
  const [gain, setGain] = useState(globalThis.audioBoostProcessor?.gainNode?.gain?.value ?? 3.5);

  const { flags } = React.useContext(AppContext);
  const debug = flags?.debugMode;

  // Match the sibling controls in the recorder footer (RecordingButton/PaperAirplane)
  // so the call button paints at the correct size on first frame instead of mounting
  // at 50px and resizing the surrounding row once it appears.
  const newDesign = !!flags.newDesign;
  const buttonSize = newDesign ? "38px" : "50px";
  const iconFontSize = newDesign ? "18px" : "20px";

  const buttonProps = {
    width: buttonSize,
    height: buttonSize,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "100%",
    p: 0,
  };

  const errorButtonProps = {
    ...buttonProps,
    background: theme.palette.error.light,
    "&:hover": {
      background: theme.palette.error.main,
    },
  };

  const successButtonProps = {
    ...buttonProps,
    background: theme.palette.success.light,
    "&:hover": {
      background: theme.palette.success.main,
    },
  };

  const makeCallAndListen = async () => {
    try {
      await createCallAndListen({
        fromAccountId,
        toAccountId,
        feedId,
      });
      ampli.dispatchCallPlaced();
      setShowRingingTimeBox(() => false);
    } catch (error) {
      ampli.dispatchCallFailed();
      setShowRingingTimeBox(() => false);
      setActiveCallRecord(() => false);
      endCall("errored");
    }
  };

  const startCall = async () => {
    if (activeCallRecord) {
      log.warn("Call already in progress, ignoring call request");
      return;
    }
    setActiveCallRecord(() => true);
    setDisabledButton(() => true);
    await makeCallAndListen();
    setShowRingingTimeBox(() => true);
    play();
    setTimeout(() => {
      setDisabledButton(() => false);
    }, 2000);
  };

  const endCall = (status: CallConfigurationState) => {
    stop();
    setModalOpen(() => false);
    setShowCallTimeBox(() => false);
    setShowRingingTimeBox(() => false);
    setActiveCall(() => false);
    setRingingSeconds(() => 0);
    setCallTimeSeconds(() => 0);
    disconnectCall(status);
    setActiveCallRecord(() => false);
  };

  /**
   * Watch for accepted and start call timer and stop ringing timer
   */
  useEffect(() => {
    if (callTextStatus === "accepted" && !showCallTimeBox) {
      stop();
      setShowRingingTimeBox(() => false);
      setRingingSeconds(() => 0);
      setShowCallTimeBox(() => true);
    }
  }, [callTextStatus, showCallTimeBox]);

  useEffect(() => {
    if (!debug || callTextStatus !== "accepted" || !globalThis.audioBoostProcessor) return;

    const processor = globalThis.audioBoostProcessor;
    const inputCanvas = document.getElementById("rawMeter") as HTMLCanvasElement;
    const outputCanvas = document.getElementById("procMeter") as HTMLCanvasElement;
    if (!inputCanvas || !outputCanvas) return;

    const inputCtx = inputCanvas.getContext("2d");
    const outputCtx = outputCanvas.getContext("2d");
    const inputData = new Uint8Array(processor.inputAnalyser.frequencyBinCount);
    const outputData = new Uint8Array(processor.outputAnalyser.frequencyBinCount);

    let animationFrame: number;

    const draw = () => {
      processor.inputAnalyser.getByteTimeDomainData(inputData);
      processor.outputAnalyser.getByteTimeDomainData(outputData);

      if (inputCtx) {
        const rms = Math.sqrt(
          inputData.reduce((acc, val) => acc + Math.pow((val - 128) / 128, 2), 0) / inputData.length,
        );
        inputCtx.clearRect(0, 0, inputCanvas.width, inputCanvas.height);
        inputCtx.fillStyle = "lime";
        inputCtx.fillRect(0, 0, rms * inputCanvas.width, inputCanvas.height);
      }

      if (outputCtx) {
        const rms = Math.sqrt(
          outputData.reduce((acc, val) => acc + Math.pow((val - 128) / 128, 2), 0) / outputData.length,
        );
        outputCtx.clearRect(0, 0, outputCanvas.width, outputCanvas.height);
        outputCtx.fillStyle = "cyan";
        outputCtx.fillRect(0, 0, rms * outputCanvas.width, outputCanvas.height);
      }

      animationFrame = requestAnimationFrame(draw);
    };

    draw();

    return () => cancelAnimationFrame(animationFrame);
  }, [debug, callTextStatus]);

  /**
   * Handle hands free off state
   */
  useEffect(() => {
    if (modalOpen && !handsFreeEnabled) {
      endCall("disconnected");
    }
  }, [handsFreeEnabled, activeCall]);

  /**
   * Handle Ringing Timeout
   */
  useEffect(() => {
    if (ringingSeconds && ringingSeconds >= maxCallTime - 1) {
      endCall("rejected");
    }
  }, [ringingSeconds]);

  // The parent layout always reserves a slot for the call button. When hands-free
  // is unavailable, render a sized-but-invisible placeholder so the row does not
  // jump as the underlying account record finishes loading. Without this, the
  // surrounding view shifts after the user has already started reading it.
  if (!handsFreeEnabled) {
    return (
      <Box
        aria-hidden="true"
        sx={{
          width: buttonSize,
          height: buttonSize,
          flexShrink: 0,
          visibility: "hidden",
          pointerEvents: "none",
        }}
      />
    );
  }

  return (
    <>
      <SimpleModal
        sx={{ width: "400px" }}
        open={modalOpen}
        disableClose={true}
        closeModal={() => setModalOpen(() => false)}
      >
        <TimeBox
          setValue={(value: number) => {
            if (showCallTimeBox) {
              setCallTimeSeconds(value);
            } else {
              setCallTimeSeconds(0);
            }
          }}
          active={showCallTimeBox}
        />
        <TimeBox
          setValue={(value: number) => {
            if (showRingingTimeBox) {
              setRingingSeconds(value);
            } else {
              setRingingSeconds(0);
            }
          }}
          active={showRingingTimeBox}
        />
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
              {name}
            </Typography>
            {callTextStatus === "ringing" && (
              <Box sx={{ display: "flex", gap: 2 }}>
                <Box>Ringing...</Box>
                <Box>{getPaddedTime(maxCallTime - ringingSeconds)}</Box>
              </Box>
            )}

            {callTextStatus === "accepted" && (
              <Box sx={{ display: "flex", gap: 2 }}>
                <Box>Connected</Box>
                <Box>{getPaddedTime(callTimeSeconds)}</Box>
              </Box>
            )}
          </Box>
          <Box>
            <Button
              className="basic-button"
              sx={{ ...errorButtonProps }}
              disabled={disabledButton}
              onClick={() => {
                setModalOpen(() => false);
                endCall("canceled");
              }}
            >
              <CallEndIcon sx={{ fontSize: iconFontSize }} />
            </Button>
          </Box>
        </Box>
        {debug && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>Audio Debug</Typography>
            <canvas id="rawMeter" width={200} height={10} style={{ background: "#222", marginTop: 4 }} />
            <canvas id="procMeter" width={200} height={10} style={{ background: "#222", marginTop: 4 }} />

            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: "12px", color: "#ccc" }}>Gain</Typography>
              <input
                type="range"
                min={0}
                max={6}
                step={0.1}
                defaultValue={globalThis.audioBoostProcessor?.gainNode?.gain?.value ?? 3.5}
                onChange={(e) => {
                  const newGain = parseFloat(e.target.value);
                  globalThis.audioBoostProcessor?.setGainLevel(newGain);
                  setGain(newGain);
                }}
                style={{ width: "100%" }}
              />
              <Typography>Gain: {gain.toFixed(1)}</Typography>
            </Box>
          </Box>
        )}
      </SimpleModal>

      {activeCall ? (
        <Button
          type="button"
          className={newDesign ? undefined : "basic-button"}
          sx={{ ...errorButtonProps }}
          disabled={disabledState}
          onClick={() => {
            setModalOpen(() => false);
            endCall("canceled");
          }}
        >
          <CallEndIcon sx={{ fontSize: iconFontSize }} />
        </Button>
      ) : (
        <Button
          type="button"
          className={newDesign ? undefined : "basic-button"}
          sx={{ ...successButtonProps }}
          disabled={disabledState}
          onClick={async () => {
            setDisabledButton(() => true);
            setModalOpen(() => true);
            await startCall();
          }}
        >
          <CallIcon sx={{ fontSize: iconFontSize }} />
        </Button>
      )}
    </>
  );
}
